import { connectorClient } from "@connector/connectorClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const connectorKeys = {
  list: (sessionKey: string) => ["connectors", sessionKey] as const,
};

/**
 * How often the paired-connector list re-asks. Matches `captureQueries`' live
 * node list: both are watched states with a server-side life of their own, and
 * without a tick the card would hold the status it first rendered — Updating…
 * would never turn back into Connected, and a fresh failure would never appear
 * at all. `AdminShell` keeps a panel mounted once opened, so this polls for the
 * rest of the admin session rather than only while Connect is on screen; that
 * is the same bargain `useCaptureNodes` makes, and it is what makes the state
 * observable when someone comes back to the tab.
 */
const CONNECTORS_REFETCH_MS = 30_000;

/**
 * The paired-connector list. Plain `useQuery` (not suspense): it lives inside
 * the Connect panel's cards rather than owning a panel, and is gated behind
 * profile-select via `enabled` — neither of which fits the suspense + panel
 * error-boundary shape the feed panels (facts/tasks/memos) use.
 */
export const useConnectorsList = (sessionKey: string, enabled: boolean) =>
  useQuery({
    queryKey: connectorKeys.list(sessionKey),
    queryFn: connectorClient.listConnectors,
    enabled,
    refetchInterval: CONNECTORS_REFETCH_MS,
  });

export const useMintPairingCode = () =>
  useMutation({
    mutationFn: connectorClient.mintPairingCode,
    meta: { action: "create a pairing code" },
  });

export const useUnpairConnector = (sessionKey: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: connectorClient.unpair,
    meta: { action: "unpair the connector" },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: connectorKeys.list(sessionKey),
      }),
  });
};

export const useDispatch = () =>
  useMutation({
    mutationFn: connectorClient.dispatch,
    meta: { action: "dispatch that command" },
  });
